import React from 'react';

import { CSSInterpolation } from '@emotion/css/create-instance';
import { css } from '@emotion/react';
import { CssVars, Modal, NeutralColorToken, TextButton, Type } from '@robinhood/design';
import Cookies from 'js-cookie';
import getConfig from 'next/config';

import Button from './Button';
import { CkBadgeCookieKey, CkPayloadCookieKey, CkSubIdCookieKey, X1InviteCookieKey } from './NavigationProvider';
import TextInput from './TextInput';
import { LOCAL_STORAGE_AUTH_STATE_KEY, SheriffTokenResponse, clearAuthState, setAuthState } from 'src/state/auth/auth';

const { publicRuntimeConfig } = getConfig();

interface PropsIface extends React.ComponentProps<'div'> {
  overrideRootStyles?: CSSInterpolation;
}

const DebugMenu = ({ overrideRootStyles, ...props }: PropsIface) => {
  const isDevelopment = publicRuntimeConfig.API_ENV != 'production';
  const [authToken, setAuthToken] = React.useState<string>('');
  const [tokenModalOpen, setTokenModalOpen] = React.useState<boolean>(false);

  const onShowTokenModal = React.useCallback(() => {
    setTokenModalOpen(true);
  }, []);

  const onDismissTokenModal = React.useCallback(() => {
    setTokenModalOpen(false);
  }, []);

  const onChangeToken = React.useCallback(({ value }: { value: string }) => {
    setAuthToken(value);
  }, []);

  const onSetToken = React.useCallback(() => {
    setAuthState(JSON.parse(authToken) as SheriffTokenResponse);
    window.location.reload();
  }, [authToken]);

  const onClearToken = React.useCallback(() => {
    clearAuthState();
    window.location.reload();
  }, []);

  const onClearAttribution = React.useCallback(() => {
    Cookies.remove(CkSubIdCookieKey);
    Cookies.remove(CkBadgeCookieKey);
    Cookies.remove(CkPayloadCookieKey);
    Cookies.remove(X1InviteCookieKey);
    window.location.reload();
  }, []);

  return isDevelopment ? (
    <div css={[root, overrideRootStyles]} {...props}>
      <TextButton label='Clear auth token' variant='standalone' type='button' onClick={onClearToken} />
      <Type.Text size='S'> | </Type.Text>
      <TextButton label='Set auth token' variant='standalone' type='button' onClick={onShowTokenModal} />
      <Type.Text size='S'> | </Type.Text>
      <TextButton label='Clear attribution' variant='standalone' type='button' onClick={onClearAttribution} />
      {tokenModalOpen && (
        <Modal width='wide' overrideModalStyles={modal} onClose={onDismissTokenModal}>
          <TextInput placeholder={LOCAL_STORAGE_AUTH_STATE_KEY} value={authToken} onChange={onChangeToken} />
          <Button label='Continue' width='full' type='button' onClick={onSetToken} />
          <Button
            label='Dismiss'
            width='full'
            type='button'
            onClick={onDismissTokenModal}
            foregroundColor={NeutralColorToken.Fg1}
          />
        </Modal>
      )}
    </div>
  ) : null;
};

const root = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: ${CssVars.neutral.bg2};
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: center;
  align-items: center;
  padding: 8px;
  z-index: 1000;
  text-align: center;
`;

const modal = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
`;

export default DebugMenu;
